import React from 'react';
import { format } from 'date-fns';
import { parseUTCTimestamp } from '../utils/dateUtils';
import type { Todo, ReviewEntry } from '../types';
import {
  SunIcon,
  CheckIcon,
  PlusIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

interface DaySummaryProps {
  date: Date;
  morningReflections?: ReviewEntry[];
  morningMood?: string;
  morningEnergy?: string;
  tasksCompleted: Todo[];
  tasksCreated: Todo[];
  dayStartedTime?: string;
  onTaskClick?: (task: Todo) => void;
  onNext: () => void;
}

export const DaySummary: React.FC<DaySummaryProps> = ({
  date,
  morningReflections,
  morningMood,
  morningEnergy,
  tasksCompleted,
  tasksCreated,
  dayStartedTime,
  onTaskClick,
  onNext
}) => {
  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Day Summary
        </h2>
        <p className="text-lg text-gray-600">
          {format(date, 'EEEE, MMMM d, yyyy')}
        </p>
      </div>

      {/* Morning Start Section */}
      {dayStartedTime && (
        <div className="bg-yellow-50 rounded-lg p-6 mb-6">
          <div className="flex items-center space-x-2 mb-4">
            <SunIcon className="h-6 w-6 text-yellow-600" />
            <h3 className="text-lg font-semibold text-gray-900">Morning Start</h3>
            <span className="text-sm text-gray-500">
              {format(parseUTCTimestamp(dayStartedTime), 'h:mm a')}
            </span>
          </div>

          {(morningMood || morningEnergy) && (
            <div className="flex space-x-4 mb-4">
              {morningMood && (
                <div className="text-sm">
                  <span className="font-medium text-gray-700">Mood:</span>{' '}
                  <span className="text-gray-600">{morningMood}</span>
                </div>
              )}
              {morningEnergy && (
                <div className="text-sm">
                  <span className="font-medium text-gray-700">Energy:</span>{' '}
                  <span className="text-gray-600">{morningEnergy}</span>
                </div>
              )}
            </div>
          )}

          {morningReflections && morningReflections.some(e => e.response?.trim()) && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700">Morning Reflections</h4>
              {morningReflections.filter(e => e.response?.trim()).map((entry, i) => (
                <div key={i} className="border-l-2 border-yellow-300 pl-3">
                  <p className="text-sm font-medium text-gray-600 mb-1">
                    {entry.prompt}
                  </p>
                  <p className="text-sm text-gray-700">{entry.response}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tasks Completed Section */}
      <div className="bg-green-50 rounded-lg p-6 mb-6">
        <div className="flex items-center space-x-2 mb-4">
          <CheckIcon className="h-6 w-6 text-green-600" />
          <h3 className="text-lg font-semibold text-gray-900">
            Tasks Completed ({tasksCompleted.length})
          </h3>
        </div>

        {tasksCompleted.length > 0 ? (
          <div className="space-y-2">
            {tasksCompleted.map(task => (
              <div
                key={task.id}
                className={`flex items-start space-x-2 p-2 rounded-md transition-all ${
                  onTaskClick ? 'cursor-pointer hover:bg-green-100 hover:shadow-md' : ''
                }`}
                onClick={() => onTaskClick?.(task)}
              >
                <CheckIcon className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{task.title}</p>
                  {task.description && (
                    <p className="text-sm text-gray-600">{task.description}</p>
                  )}
                  {task.completed_date && (
                    <p className="text-xs text-gray-500 mt-1">
                      Completed at {format(parseUTCTimestamp(task.completed_date), 'h:mm a')}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-600">No tasks completed today</p>
        )}
      </div>

      {/* Tasks Created Section */}
      <div className="bg-blue-50 rounded-lg p-6 mb-6">
        <div className="flex items-center space-x-2 mb-4">
          <PlusIcon className="h-6 w-6 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">
            Tasks Created ({tasksCreated.length})
          </h3>
        </div>

        {tasksCreated.length > 0 ? (
          <div className="space-y-2">
            {tasksCreated.map(task => (
              <div
                key={task.id}
                className={`flex items-start space-x-2 p-2 rounded-md transition-all ${
                  onTaskClick ? 'cursor-pointer hover:bg-blue-100 hover:shadow-md' : ''
                }`}
                onClick={() => onTaskClick?.(task)}
              >
                <PlusIcon className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{task.title}</p>
                  {task.description && (
                    <p className="text-sm text-gray-600">{task.description}</p>
                  )}
                  <div className="flex items-center space-x-3 mt-1 text-xs text-gray-500">
                    {task.horizon && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        task.horizon === 'now' ? 'bg-red-100 text-red-800' :
                        task.horizon === 'next' ? 'bg-blue-100 text-blue-800' :
                        task.horizon === 'soon' ? 'bg-yellow-100 text-yellow-800' :
                        task.horizon === 'later' ? 'bg-gray-100 text-gray-600' :
                        'bg-gray-100 text-gray-500'
                      }`}>
                        {task.horizon}
                      </span>
                    )}
                    {task.deadline && (
                      <span className="flex items-center">
                        <ClockIcon className="w-3 h-3 mr-1" />
                        Due {format(new Date(task.deadline), 'MMM d')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-600">No tasks created today</p>
        )}
      </div>

      {/* Next Button */}
      <div className="flex justify-end">
        <button
          onClick={onNext}
          className="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 font-medium"
        >
          Continue to Evening Reflection
        </button>
      </div>
    </div>
  );
};